import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, EmptyState, ErrorState } from "@/components/common";
import { Bell, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Notifications() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["notifications-page"],
    queryFn: async () => (await api.get("/notifications")).data,
  });

  const markRead = useMutation({
    mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifications-page"] }); qc.invalidateQueries({ queryKey: ["notifications-count"] }); },
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState label="Failed to load notifications" onRetry={() => refetch()} />;

  const items = data?.items ?? [];
  const levelColor = { info: "border-l-blue-500", success: "border-l-emerald-500", warning: "border-l-amber-500", error: "border-l-rose-500" };

  return (
    <div>
      <PageHeader title="Notifications" subtitle="System and trading alerts" icon={Bell} testId="notifications-header" demo={false} />
      {items.length === 0 ? (
        <Panel><EmptyState label="No notifications" icon={Bell} /></Panel>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {items.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} className={cn("flex items-start justify-between gap-4 rounded-lg border border-[var(--border-subtle)] border-l-2 bg-[var(--card)] px-4 py-3", levelColor[n.level], n.read && "opacity-50")}>
              <div>
                <div className="text-sm font-medium text-slate-200">{n.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>
                <div className="text-[10px] text-slate-600 font-mono mt-1">{new Date(n.created_at).toLocaleString("en-IN")}</div>
              </div>
              {!n.read && (
                <button data-testid={`mark-read-${n.id}`} onClick={() => markRead.mutate(n.id)} className="shrink-0 flex items-center gap-1 text-[11px] text-slate-500 hover:text-emerald-400">
                  <Check className="w-3.5 h-3.5" /> Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
